import React, {useCallback, useEffect, useRef, useState} from 'react';

// ---------- Types ----------
interface RangeSliderProps {
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  value?: number;
  onChange?: (value: number) => void;
  formatLabel?: (value: number) => string;
  ariaLabel?: string;
  reverse?: boolean;
}

// ---------- Utility: snap value to step ----------
const snapToStep = (value: number, step: number, min: number, max: number) => {
  const snapped = Math.round((value - min) / step) * step + min;
  return Math.min(max, Math.max(min, snapped));
};

const RangeSlider: React.FC<RangeSliderProps> = ({
  min = 0,
  max = 1000,
  step = 50,
  defaultValue = min,
  value: controlledValue,
  onChange,
  formatLabel = v => v.toString(),
  ariaLabel = 'RangeSlider value',
  reverse = true,
}) => {
  // ---------- State ----------
  const [internalValue, setInternalValue] = useState<number>(
    snapToStep(defaultValue, step, min, max),
  );
  const value = controlledValue ?? internalValue;

  // Refs
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const activeTrackRef = useRef<HTMLDivElement>(null);
  const valueLabelRef = useRef<HTMLSpanElement>(null);
  const isDragging = useRef(false);
  const currentValueRef = useRef(value);
  const startXRef = useRef(0);
  const startValueRef = useRef(0);

  // Sync currentValueRef with value
  useEffect(() => {
    currentValueRef.current = value;
  }, [value]);

  // Update DOM directly without re-render
  const updateDOM = useCallback(
    (newValue: number) => {
      if (!thumbRef.current || !activeTrackRef.current || !trackRef.current)
        return;

      // Calculate percentage with padding for thumb edges
      const range = max - min;
      const percent = ((newValue - min) / range) * 100;

      if (reverse) {
        // RTL: active track grows from right, thumb position from left
        const reversedPercent = ((max - newValue) / range) * 100;
        thumbRef.current.style.left = `${reversedPercent}%`;
        activeTrackRef.current.style.width = `${100 - reversedPercent}%`;
        activeTrackRef.current.style.right = '0';
        activeTrackRef.current.style.left = 'auto';
      } else {
        // LTR: active track grows from left, thumb position from left
        thumbRef.current.style.left = `${percent}%`;
        activeTrackRef.current.style.width = `${percent}%`;
        activeTrackRef.current.style.left = '0';
        activeTrackRef.current.style.right = 'auto';
      }

      if (valueLabelRef.current) {
        valueLabelRef.current.textContent = formatLabel(newValue);
      }
    },
    [min, max, formatLabel, reverse],
  );

  // Get value from position
  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return currentValueRef.current;

      const rect = trackRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));

      const fraction = reverse ? 1 - x / rect.width : x / rect.width;
      const raw = fraction * (max - min) + min;
      return snapToStep(raw, step, min, max);
    },
    [min, max, step, reverse],
  );

  // ---------- Mouse event handlers (using refs, no React events) ----------
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      isDragging.current = true;

      const newValue = getValueFromPosition(e.clientX);

      currentValueRef.current = newValue;
      updateDOM(newValue);

      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }

      onChange?.(newValue);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const newValue = getValueFromPosition(e.clientX);

      if (newValue !== currentValueRef.current) {
        currentValueRef.current = newValue;
        updateDOM(newValue);

        if (controlledValue === undefined) {
          setInternalValue(newValue);
        }
        onChange?.(newValue);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDragging.current) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      isDragging.current = false;

      const finalValue = snapToStep(currentValueRef.current, step, min, max);
      if (finalValue !== currentValueRef.current) {
        currentValueRef.current = finalValue;
        updateDOM(finalValue);
        if (!controlledValue) {
          setInternalValue(finalValue);
        }
        onChange?.(finalValue);
      }
    };

    track.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove, true);
    window.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      track.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove, true);
      window.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [
    getValueFromPosition,
    updateDOM,
    onChange,
    controlledValue,
    step,
    min,
    max,
  ]);

  // ---------- Touch event handlers ----------
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      isDragging.current = true;

      const touch = e.touches[0];

      const newValue = getValueFromPosition(touch.clientX);

      currentValueRef.current = newValue;
      updateDOM(newValue);

      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }

      onChange?.(newValue);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const touch = e.touches[0];
      const newValue = getValueFromPosition(touch.clientX);

      if (newValue !== currentValueRef.current) {
        currentValueRef.current = newValue;
        updateDOM(newValue);

        if (controlledValue === undefined) {
          setInternalValue(newValue);
        }
        onChange?.(newValue);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isDragging.current) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      isDragging.current = false;

      const finalValue = snapToStep(currentValueRef.current, step, min, max);
      if (finalValue !== currentValueRef.current) {
        currentValueRef.current = finalValue;
        updateDOM(finalValue);
        if (!controlledValue) {
          setInternalValue(finalValue);
        }
        onChange?.(finalValue);
      }
    };

    track.addEventListener('touchstart', handleTouchStart, {
      passive: false,
      capture: true,
    });
    track.addEventListener('touchmove', handleTouchMove, {
      passive: false,
      capture: true,
    });
    track.addEventListener('touchend', handleTouchEnd, {
      passive: false,
      capture: true,
    });

    return () => {
      track.removeEventListener('touchstart', handleTouchStart, true);
      track.removeEventListener('touchmove', handleTouchMove, true);
      track.removeEventListener('touchend', handleTouchEnd, true);
    };
  }, [
    getValueFromPosition,
    updateDOM,
    onChange,
    controlledValue,
    step,
    min,
    max,
  ]);

  // ---------- Keyboard handlers ----------
  const handleKeyDown = (e: React.KeyboardEvent) => {
    let delta = 0;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      delta = reverse ? -step : step;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      delta = reverse ? step : -step;
    } else if (e.key === 'Home') {
      delta = min - value;
    } else if (e.key === 'End') {
      delta = max - value;
    } else {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const newValue = snapToStep(value + delta, step, min, max);
    currentValueRef.current = newValue;
    updateDOM(newValue);

    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  // Calculate percentage for initial render
  const percent = reverse
    ? ((max - value) / (max - min)) * 100
    : ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div className="px-2.5">
        <div
          ref={trackRef}
          className={`relative flex items-center h-12 cursor-pointer select-none`}
          style={{touchAction: 'none', userSelect: 'none'}}
          onKeyDown={handleKeyDown}
        >
          {/* Track background */}
          <div className="absolute inset-x-0 h-2 bg-gray-200 rounded-full pointer-events-none" />

          {/* Active range - constrained within track */}
          <div
            ref={activeTrackRef}
            className="absolute h-2 rounded-full pointer-events-none bg-farm-shop-500"
            style={{
              right: 0,
              width: `${100 - percent}%`,
            }}
          />

          {/* Thumb - transform accounts for centering */}
          <div
            ref={thumbRef}
            role="RangeSlider"
            tabIndex={0}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-valuetext={formatLabel(value)}
            aria-label={ariaLabel}
            aria-orientation="horizontal"
            className={`absolute w-6 h-6 -translate-x-1/2 rounded-full shadow-lg pointer-events-none`}
            style={{
              left: `${percent}%`,
              background: 'white',
              border: '6px solid #669f2a',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Min / Max labels */}
      <div className="flex justify-between text-xs font-normal text-gray-600">
        <span>{formatLabel(min)}</span>
        <span>{formatLabel(max)}</span>
      </div>
    </div>
  );
};

export default RangeSlider;
